<template>
  <div class="app-wrapper">
    <header class="top-header">
      <div class="header-brand">
        <div class="brand-mark">T</div>
        <div class="brand-info">
          <span class="brand-name">thxily</span>
          <span class="brand-sub">管理コンソール</span>
        </div>
      </div>
      <button class="back-btn" v-on:click="goBack">← メニューへ戻る</button>
    </header>
    <main class="main-content">
      <div class="white-card" :class="{ processing: isProcessing }">
        <h2 class="page-title">クレジットカード管理</h2>

        <!-- ローディング画面 -->
        <div v-if="currentView === 'loading'" class="loading-screen">
          <div class="loading-spinner-large"></div>
          <p>カード情報を読み込み中...</p>
        </div>

        <!-- カード情報表示画面 -->
        <div v-else-if="currentView === 'display'">
          <div class="section-header">
            <h3 class="section-title">登録済みクレジットカード</h3>
            <button type="button" class="btn-danger-subtle" @click="confirmDelete">
              カードを削除
            </button>
          </div>
          <div class="card-details">
            <div class="detail-row">
              <span class="detail-label">カード番号</span>
              <span class="detail-value">{{ cardInfo.card_no_masked }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">有効期限</span>
              <span class="detail-value">{{ cardInfo.expire_masked }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">カード名義人</span>
              <span class="detail-value">{{ cardInfo.holder_name }}</span>
            </div>
            <div class="detail-row" v-if="cardInfo.brand">
              <span class="detail-label">ブランド</span>
              <span class="detail-value">{{ cardInfo.brand }}</span>
            </div>
            <div class="detail-row" v-if="cardInfo.updated">
              <span class="detail-label">最終更新</span>
              <span class="detail-value">{{ formatDate(cardInfo.updated) }}</span>
            </div>
          </div>
          <div class="btn-row">

            <button type="button" class="btn-primary" @click="startEdit">
              カード情報を変更
            </button>
          </div>
        </div>
        <!-- カード編集・登録画面 -->
        <div v-else-if="currentView === 'edit'">
          <h3 class="section-title">{{ hasExistingCard ? 'クレジットカード変更' : 'クレジットカード登録' }}</h3>
          <form @submit.prevent="saveCard">
            <div class="form-group">
              <label class="form-label">カード番号 <span class="required">*</span></label>
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': cardData.number && !isCardNumberValid }"
                v-model="cardData.number"
                @input="formatCardNumber"
                placeholder="1234 5678 9012 3456"
                maxlength="19"
                :disabled="isProcessing"
                required
              />
              <div class="form-help" v-if="!cardData.number || isCardNumberValid">16桁のカード番号を入力してください</div>
              <div class="form-error" v-if="cardData.number && !isCardNumberValid">カード番号は16桁の数字で入力してください</div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">有効期限 <span class="required">*</span></label>
                <input
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': cardData.expiry && !isExpiryValid }"
                  v-model="cardData.expiry"
                  @input="formatExpiry"
                  placeholder="MM/YY"
                  maxlength="5"
                  :disabled="isProcessing"
                  required
                />
                <div class="form-error" v-if="cardData.expiry && !isExpiryValid">有効期限が正しくありません</div>
              </div>
              <div class="form-group">
                <label class="form-label">CVV <span class="required">*</span></label>
                <input
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': cardData.cvv && !isCvvValid }"
                  v-model="cardData.cvv"
                  @input="formatCVV"
                  placeholder="123"
                  maxlength="4"
                  :disabled="isProcessing"
                  required
                />
                <div class="form-help" v-if="!cardData.cvv || isCvvValid">カード裏面の3-4桁の番号</div>
                <div class="form-error" v-if="cardData.cvv && !isCvvValid">CVVは3-4桁の数字で入力してください</div>
              </div>
            </div>
            <div class="form-group">
              <label class="form-label">カード名義人 <span class="required">*</span></label>
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': cardData.name && !isNameValid }"
                v-model="cardData.name"
                placeholder="YAMADA TARO"
                :disabled="isProcessing"
                required
              />
              <div class="form-help" v-if="!cardData.name || isNameValid">カードに記載されている名前をローマ字で入力</div>
              <div class="form-error" v-if="cardData.name && !isNameValid">カード名義人はローマ字（英数字）で入力してください</div>
            </div>
            <div class="btn-row">
              <button type="button" class="btn-secondary" @click="cancelEdit" :disabled="isProcessing">
                キャンセル
              </button>
              <button type="submit" class="btn-primary" :disabled="isProcessing || !isFormValid">
                <span v-if="isProcessing" class="loading-spinner"></span>
                {{ isProcessing ? (processingMessage || '処理中...') : (hasExistingCard ? 'カード情報を更新' : 'カードを登録') }}
              </button>
            </div>
          </form>
        </div>

        <!-- 処理完了画面 -->
        <div v-else-if="currentView === 'success'" class="success-message">
          <div class="result-icon success-icon">✅</div>
          <h3>{{ successMessage }}</h3>
          <p>処理が正常に完了しました。</p>
          <div class="btn-row">
            <button class="btn-primary" @click="loadCardInfo">
              カード情報を確認
            </button>
          </div>
        </div>

        <!-- エラー画面 -->
        <div v-else-if="currentView === 'error'" class="error-message">
          <div class="result-icon error-icon">❌</div>
          <h4>エラーが発生しました</h4>
          <p>{{ errorMessage }}</p>
          <div class="btn-row">
            <button class="btn-secondary" @click="goBack">
              戻る
            </button>
            <button class="btn-primary" @click="retry">
              再試行
            </button>
          </div>
        </div>

        <!-- 削除確認ダイアログ -->
        <div v-if="showDeleteDialog" class="modal-overlay">
          <div class="modal-dialog" @click.stop>
            <div class="modal-header">
              <h5>カード削除の確認</h5>
            </div>
            <div class="modal-body">
              <p>登録されているクレジットカード情報を削除してもよろしいですか？</p>
              <p class="text-warning"><strong>この操作は元に戻すことができません。</strong></p>
            </div>
            <div class="modal-footer" @click.stop>
              <button class="btn-secondary modal-btn" @click.stop="cancelDelete">
                キャンセル
              </button>
              <button
                class="btn-danger modal-btn"
                @click.prevent.stop="deleteCard"
                :disabled="isProcessing"
                style="pointer-events: auto; z-index: 9999; position: relative; cursor: pointer;">
                <span v-if="isProcessing" class="loading-spinner"></span>
                {{ isProcessing ? '削除中...' : '削除する' }}
              </button>
            </div>
          </div>
        </div>

      </div>
    </main>
  </div>
</template>

<script>
import axios from "axios";
import store from "@/store";

export default {
  name: 'CreditCardView',
  mounted() {

    if (!window.Fincode) {
      const script = document.createElement('script');
      script.src = this.fincodeScriptUrl;
      script.async = true;
      document.head.appendChild(script);
    }

    this.loadCardInfo();
  },
  data() {
    return {
      currentView: 'loading', // 'loading', 'display', 'edit', 'success', 'error'
      isProcessing: false,
      errorMessage: '',
      successMessage: '',
      showDeleteDialog: false,
      hasExistingCard: false,
      cardInfo: {},
      cardData: {
        number: '',
        expiry: '',
        cvv: '',
        name: ''
      },
      processingMessage: '',
      fincodeScriptUrl: process.env.VUE_APP_FINCODE_SCRIPT_URL || 'https://js.test.fincode.jp/v1/fincode.js'
    };
  },
  computed: {
    isFormValid() {
      return (
        this.isCardNumberValid &&
        this.isExpiryValid &&
        this.isCvvValid &&
        this.isNameValid
      );
    },
    isCardNumberValid() {
      const number = this.cardData.number.replace(/\s/g, '');
      return number.length === 16 && /^\d+$/.test(number);
    },
    isExpiryValid() {
      if (this.cardData.expiry.length !== 5) return false;
      const [month, year] = this.cardData.expiry.split('/');
      const monthNum = parseInt(month);
      const yearNum = parseInt('20' + year);
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;

      return (
        monthNum >= 1 && monthNum <= 12 &&
        (yearNum > currentYear || (yearNum === currentYear && monthNum >= currentMonth))
      );
    },
    isCvvValid() {
      return this.cardData.cvv.length >= 3 && this.cardData.cvv.length <= 4 && /^\d+$/.test(this.cardData.cvv);
    },
    isNameValid() {
      return this.cardData.name.trim().length > 0 && /^[A-Za-z\s]+$/.test(this.cardData.name.trim());
    }
  },
  methods: {
    async loadCardInfo() {
      this.currentView = 'loading';
      this.errorMessage = '';

      try {
        const response = await axios.get("/user_card_info");

        if (response.data.status === "SUCCESS") {
          if (response.data.has_card) {
            this.cardInfo = response.data.data;
            this.hasExistingCard = true;
            this.currentView = 'display';
          } else {
            this.hasExistingCard = false;
            this.currentView = 'edit';
          }
        } else {
          throw new Error(response.data.message || 'カード情報の取得に失敗しました');
        }
      } catch (error) {
        console.error('Card info loading error:', error);
        this.errorMessage = error.response?.data?.detail || error.message || 'カード情報の読み込み中にエラーが発生しました';
        this.currentView = 'error';
      }
    },

    startEdit() {
      this.clearCardData();
      this.currentView = 'edit';
    },

    cancelEdit() {
      if (this.hasExistingCard) {
        this.currentView = 'display';
      } else {
        this.goBack();
      }
    },

    async saveCard() {
      if (!this.isFormValid) {
        this.errorMessage = this.getValidationErrorMessage();
        return;
      }

      this.isProcessing = true;
      this.errorMessage = '';
      this.processingMessage = 'カード情報を登録中...';

      const response = await axios.get("/fincode_customer_id");
      var customer_id = '';
      if (response.data.status === "SUCCESS") {
        customer_id = response.data.customer_id;
      } else {
        throw new Error(response.data.message || '顧客情報の取得に失敗しました');
      }

      try {
        const payload = {
          customer_id: customer_id,
          card_no: this.cardData.number.replace(/\s/g, ''),
          default_flag: '1',
          expire: this.cardData.expiry.includes('/') ?
            this.cardData.expiry.split('/')[1] + this.cardData.expiry.split('/')[0] :
            this.cardData.expiry, // MM/YY -> YYMM
          holder_name: this.cardData.name.trim(),
          security_code: this.cardData.cvv,
          type: '1',
        };

        let fincode = window.Fincode(process.env.VUE_APP_FINCODE_PUBLIC_KEY);
        const self = this; // thisコンテキストを保持

        fincode.cards(payload,
            function (status, response) {
              console.log("状態:", status);
              console.log("結果:", response);
              if (status === 200) {
                // 新しいカードが登録成功した場合、サーバー側でデフォルトフラグがOFFの古いカードを削除
                self.processingMessage = '古いカード情報を整理中...';

                self.cleanupNonDefaultCards()
                    .then((_) => {
                      self.successMessage = self.hasExistingCard ?
                        "カード情報を更新しました" : "カード情報を登録しました";
                      self.hasExistingCard = true;
                      self.currentView = 'success';
                      self.clearCardData();
                      self.processingMessage = '';
                      self.isProcessing = false;
                    })
                    .catch((error) => {
                        console.warn('クリーンアップ失敗（メイン処理は正常）:', error);
                        // クリーンアップが失敗してもメイン処理は成功として扱う
                        self.successMessage = self.hasExistingCard ?
                          "カード情報を更新しました" : "カード情報を登録しました";
                        self.hasExistingCard = true;
                        self.currentView = 'success';
                        self.clearCardData();
                        self.processingMessage = '';
                        self.isProcessing = false;
                    });
              } else {
                // リクエストエラー時の処理
                console.error("Fincode API error:", response);
                self.errorMessage = 'カード情報の保存に失敗しました';
                self.currentView = 'error';
                self.processingMessage = '';
                self.isProcessing = false;
              }
            },
            function (error) {
              console.error("Fincode API failure:", error);
              self.errorMessage = 'カード情報の保存中にエラーが発生しました';
              self.currentView = 'error';
              self.processingMessage = '';
              self.isProcessing = false;
            }
        )

        // const response = await axios.put("/user_card_info", payload);

        // if (response.data.status === "SUCCESS") {
        //   this.successMessage = response.data.action === "updated" ?
        //     "カード情報を更新しました" : "カード情報を登録しました";
        //   this.hasExistingCard = true;
        //   this.currentView = 'success';
        //   this.clearCardData();
        // } else {
        //   throw new Error(response.data.message || 'カード情報の保存に失敗しました');
        // }
      } catch (error) {
        console.error('Card save error:', error);
        this.errorMessage = error.response?.data?.detail || error.message || 'カード情報の保存中にエラーが発生しました';
        this.currentView = 'error';
        this.processingMessage = '';
        this.isProcessing = false;
      }
    },

    confirmDelete() {
      console.log('confirmDelete() 呼び出し - showDeleteDialog:', this.showDeleteDialog);
      this.showDeleteDialog = true;
      console.log('confirmDelete() 完了 - showDeleteDialog:', this.showDeleteDialog);
    },

    cancelDelete() {
      console.log('cancelDelete() 呼び出し - showDeleteDialog:', this.showDeleteDialog);
      this.showDeleteDialog = false;
      console.log('cancelDelete() 完了 - showDeleteDialog:', this.showDeleteDialog);
    },

    testClick() {
      console.log('=== testClick() が呼ばれました！ ===');
      console.log('現在時刻:', new Date());
      console.log('isProcessing:', this.isProcessing);
      alert('テストクリックが動作しています！');
    },

    handleDeleteClick(event) {
      console.log('handleDeleteClick() 呼び出し');
      console.log('event:', event);
      console.log('isProcessing:', this.isProcessing);

      if (this.isProcessing) {
        console.log('処理中のため削除をスキップ');
        return;
      }

      this.deleteCard();
    },

    async deleteCard() {
      console.log('deleteCard() メソッド開始');
      console.log('axios.defaults.baseURL:', axios.defaults.baseURL);
      console.log('store accessToken:', store.getters.accessToken);
      this.isProcessing = true;

      try {
        console.log('API呼び出し開始: DELETE /user_card_info/cleanup with delete_all=true');
        console.log('リクエスト詳細:', {
          url: '/user_card_info/cleanup',
          params: { delete_all: true },
          baseURL: axios.defaults.baseURL
        });

        const response = await axios.delete("/user_card_info/cleanup", {
          params: { delete_all: true }
        });
        console.log('API呼び出し成功:', response);

        if (response.data.status === "SUCCESS") {
          console.log('削除成功、画面更新中');
          this.successMessage = "カード情報を削除しました";
          this.hasExistingCard = false;
          this.cardInfo = {};
          this.showDeleteDialog = false;
          this.currentView = 'success';
        } else {
          console.error('API応答エラー:', response.data);
          throw new Error(response.data.message || 'カード情報の削除に失敗しました');
        }
      } catch (error) {
        console.error('Card delete error:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        this.errorMessage = error.response?.data?.detail || error.message || 'カード情報の削除中にエラーが発生しました';
        this.showDeleteDialog = false;
        this.currentView = 'error';
      } finally {
        console.log('deleteCard() メソッド終了、isProcessing=false');
        this.isProcessing = false;
      }
    },

    // カード番号のフォーマット（4桁ごとにスペース）
    formatCardNumber() {
      let value = this.cardData.number.replace(/\s/g, '').replace(/[^0-9]/gi, '');
      if (value.length > 16) {
        value = value.substring(0, 16);
      }
      let formattedValue = value.match(/.{1,4}/g)?.join(' ') || value;
      this.cardData.number = formattedValue;
    },

    // 有効期限のフォーマット（MM/YY）
    formatExpiry() {
      let value = this.cardData.expiry.replace(/\D/g, '');
      if (value.length >= 2) {
        let month = value.substring(0, 2);
        // 月は01-12の範囲にする
        if (parseInt(month) > 12) {
          month = '12';
        }
        if (parseInt(month) < 1) {
          month = '01';
        }
        value = month + (value.length > 2 ? '/' + value.substring(2, 4) : '');
      }
      this.cardData.expiry = value;
    },

    // CVVは数字のみ
    formatCVV() {
      this.cardData.cvv = this.cardData.cvv.replace(/[^0-9]/g, '');
    },

    clearCardData() {
      this.cardData = {
        number: '',
        expiry: '',
        cvv: '',
        name: ''
      };
      this.processingMessage = '';
    },

    goBack() {
      this.$router.go(-1);
    },

    retry() {
      this.loadCardInfo();
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}/${month}/${day}`;
    },

    getValidationErrorMessage() {
      if (!this.isCardNumberValid) {
        return 'カード番号は16桁の数字で入力してください。';
      }
      if (!this.isExpiryValid) {
        return '有効期限が正しくありません。MM/YY形式で未来の日付を入力してください。';
      }
      if (!this.isCvvValid) {
        return 'CVVは3-4桁の数字で入力してください。';
      }
      if (!this.isNameValid) {
        return 'カード名義人はローマ字（英数字）で入力してください。';
      }
      return '入力内容に誤りがあります。';
    },

    async cleanupNonDefaultCards() {
      try {
        console.log('サーバー側でデフォルトフラグOFFのカードを削除中...');
        const response = await axios.delete("/user_card_info/cleanup");

        if (response.data.status === "SUCCESS") {
          console.log('クリーンアップ完了:', response.data.message);
          console.log('削除されたカード数:', response.data.deleted_count);
        } else {
          console.warn('クリーンアップに失敗:', response.data.message);
        }
      } catch (error) {
        console.warn('クリーンアップエラー:', error);
        // クリーンアップの失敗はメイン処理に影響しないため、ログのみ
      }
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;700&display=swap');

.app-wrapper {
  min-height: 100vh;
  background: #DDE2EC;
  font-family: 'Noto Sans JP', 'Segoe UI', sans-serif;
}

.top-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  height: 64px;
  background: #1E2A3A;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}

.header-brand { display: flex; align-items: center; gap: 12px; }

.brand-mark {
  width: 38px; height: 38px; border-radius: 10px;
  background: linear-gradient(135deg, #4F8EF7, #7C5CFC);
  display: flex; align-items: center; justify-content: center;
  font-size: 1.1rem; font-weight: 800; color: #fff;
}

.brand-info { display: flex; flex-direction: column; line-height: 1.2; }
.brand-name { font-size: 1rem; font-weight: 700; color: #fff; }
.brand-sub  { font-size: 0.68rem; color: #8A9BB0; }

.back-btn {
  padding: 8px 20px; border-radius: 8px;
  border: 1.5px solid #94A3B8; background: transparent;
  color: #94A3B8; font-size: 0.85rem; font-weight: 600;
  cursor: pointer; font-family: inherit;
  transition: background 0.2s, color 0.2s;
}
.back-btn:hover { background: #94A3B8; color: #1E2A3A; }

.main-content {
  max-width: 960px; margin: 0 auto;
  padding: 40px 24px 80px;
}

.white-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.10);
  padding: 32px 40px;
  position: relative;
}

.page-title {
  font-size: 1.3rem; font-weight: 700;
  color: #1E293B; margin: 0 0 24px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid #E2E8F0;
}

.section-title {
  font-size: 1rem; font-weight: 700;
  color: #1E293B; margin: 0 0 16px 0;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.section-header .section-title {
  margin: 0;
}

/* フォーム */
.form-group { margin-bottom: 20px; }

.form-label {
  display: block; font-size: 0.85rem; font-weight: 600;
  color: #475569; margin-bottom: 6px;
}

.required { color: #EF4444; }

.form-control {
  width: 100%; padding: 10px 14px; border-radius: 8px;
  border: 1.5px solid #CBD5E1; font-size: 0.9rem;
  font-family: inherit; outline: none; box-sizing: border-box;
  transition: border-color 0.2s;
}
.form-control:focus { border-color: #3B82F6; }
.form-control.is-invalid { border-color: #EF4444; }
.form-control:disabled { background: #F8FAFC; }

.form-help {
  font-size: 0.75rem; color: #94A3B8; margin-top: 4px;
}

.form-error {
  font-size: 0.75rem; color: #EF4444; margin-top: 4px; font-weight: 600;
}

.form-row {
  display: flex; gap: 16px;
}
.form-row .form-group { flex: 1; }

/* カード詳細 */
.card-details {
  background: #F8FAFC;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 24px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #E2E8F0;
}
.detail-row:last-child { border-bottom: none; }

.detail-label {
  color: #64748B; font-size: 0.85rem; font-weight: 600;
  min-width: 110px;
}

.detail-value {
  color: #1E293B; font-weight: 600; font-size: 0.9rem;
  text-align: right; flex: 1;
}

/* ボタン */
.btn-row {
  display: flex; gap: 12px; margin-top: 24px; flex-wrap: wrap;
}

.btn-primary {
  padding: 10px 24px; border-radius: 8px; border: none;
  background: #3B82F6; color: #fff;
  font-size: 0.9rem; font-weight: 600; cursor: pointer;
  font-family: inherit; transition: background 0.2s;
  display: inline-flex; align-items: center; gap: 8px;
}
.btn-primary:hover:not(:disabled) { background: #2563EB; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-secondary {
  padding: 10px 24px; border-radius: 8px;
  border: 1.5px solid #CBD5E1; background: #fff;
  color: #475569; font-size: 0.9rem; font-weight: 600;
  cursor: pointer; font-family: inherit; transition: background 0.2s;
}
.btn-secondary:hover:not(:disabled) { background: #F1F5F9; }
.btn-secondary:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-danger {
  padding: 10px 24px; border-radius: 8px; border: none;
  background: #EF4444; color: #fff;
  font-size: 0.9rem; font-weight: 600; cursor: pointer;
  font-family: inherit; transition: background 0.2s;
  display: inline-flex; align-items: center; gap: 8px;
}
.btn-danger:hover:not(:disabled) { background: #DC2626; }
.btn-danger:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-danger-subtle {
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid #FCA5A5;
  background: #FEF2F2;
  color: #DC2626;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.2s, border-color 0.2s;
}
.btn-danger-subtle:hover:not(:disabled) {
  background: #FEE2E2;
  border-color: #F87171;
}
.btn-danger-subtle:disabled { opacity: 0.6; cursor: not-allowed; }

/* ローディング */
.loading-screen {
  text-align: center;
  padding: 60px 0;
  color: #64748B;
}

.loading-spinner-large {
  display: inline-block;
  width: 40px; height: 40px;
  border: 4px solid #E2E8F0;
  border-radius: 50%;
  border-top-color: #3B82F6;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

.loading-spinner {
  display: inline-block;
  width: 14px; height: 14px;
  border: 2px solid rgba(255,255,255,0.4);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 0.8s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

/* 成功・エラーメッセージ */
.success-message, .error-message {
  text-align: center;
  padding: 24px 0;
}

.result-icon {
  font-size: 3rem;
  margin-bottom: 12px;
}

.success-message h3, .error-message h4 {
  color: #1E293B;
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0 8px 0;
}

.success-message p, .error-message p {
  color: #64748B;
  font-size: 0.9rem;
  margin-bottom: 0;
}

.success-message .btn-row, .error-message .btn-row {
  justify-content: center;
}

/* モーダル */
.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-dialog {
  background: white;
  border-radius: 12px;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.24);
  overflow: hidden;
}

.modal-header {
  background: #F8FAFC;
  padding: 20px 24px;
  border-bottom: 1.5px solid #E2E8F0;
}

.modal-header h5 {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #1E293B;
}

.modal-body {
  padding: 20px 24px;
}

.modal-body p {
  margin-bottom: 10px;
  color: #475569;
  line-height: 1.6;
  font-size: 0.9rem;
}

.text-warning {
  color: #D97706;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1.5px solid #E2E8F0;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.modal-btn {
  padding: 8px 20px;
}

.processing { opacity: 0.85; }
</style>
